/**
 * 卡片正文的 localStorage 内容寻址缓存：键 = 服务端给的 contentHash，正文变了哈希就变，永不陈旧。
 * 同源所有站点共享约 5MB 配额，所以按 LRU 控制条数与总字符数；配额满时再砍掉一半最旧的重试一次。
 * storage 注入（测试传假实现；浏览器禁用 localStorage 时传内存实现）。
 */
export const CARD_PREFIX = 'notes_card_v1:'
export const LRU_KEY = 'notes_card_lru_v1'
export const MAX_ENTRIES = 80
export const MAX_CHARS = 2000000

export function createCardCache(storage, limits = {}) {
    const maxEntries = limits.maxEntries ?? MAX_ENTRIES
    const maxChars = limits.maxChars ?? MAX_CHARS

    const readLru = () => {
        try {
            const v = JSON.parse(storage.getItem(LRU_KEY) || '[]')
            return Array.isArray(v) ? v.filter((e) => e && typeof e.h === 'string' && typeof e.n === 'number') : []
        } catch (e) {
            return []
        }
    }
    const writeLru = (lru) => {
        try { storage.setItem(LRU_KEY, JSON.stringify(lru)) } catch (e) { /* 配额满时 LRU 表写不进也不影响正文读取 */ }
    }
    const remove = (h) => {
        try { storage.removeItem(CARD_PREFIX + h) } catch (e) { /* ignore */ }
    }

    return {
        get(hash) {
            if (!hash) return null
            let v = null
            try { v = storage.getItem(CARD_PREFIX + hash) } catch (e) { return null }
            if (v == null) return null
            // 命中即挪到队尾（最近使用）
            const lru = readLru().filter((e) => e.h !== hash)
            lru.push({ h: hash, n: v.length })
            writeLru(lru)
            return v
        },
        set(hash, content) {
            if (!hash || typeof content !== 'string' || content.length > maxChars) return
            let lru = readLru().filter((e) => e.h !== hash)
            lru.push({ h: hash, n: content.length })
            let total = lru.reduce((s, e) => s + e.n, 0)
            // 淘汰最久未用（队首），直到条数与总量都在上限内；新条目在队尾不会被淘汰
            while (lru.length > 1 && (lru.length > maxEntries || total > maxChars)) {
                const old = lru.shift()
                total -= old.n
                remove(old.h)
            }
            try {
                storage.setItem(CARD_PREFIX + hash, content)
            } catch (e) {
                const drop = lru.splice(0, Math.ceil((lru.length - 1) / 2))
                drop.forEach((d) => remove(d.h))
                try {
                    storage.setItem(CARD_PREFIX + hash, content)
                } catch (e2) {
                    lru = lru.filter((x) => x.h !== hash)
                }
            }
            writeLru(lru)
        },
    }
}

/** localStorage 不可用（隐私模式 / 被禁用）时的内存替身 */
export function memoryStorage() {
    const m = new Map()
    return {
        getItem: (k) => (m.has(k) ? m.get(k) : null),
        setItem: (k, v) => { m.set(k, String(v)) },
        removeItem: (k) => { m.delete(k) },
    }
}
